import { useEffect, useState, useCallback } from "react";
import { Combobox, LegacyStack, Tag, Listbox } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  fetchUserAssets,
  fetch as fetchAllAssets,
} from "services/assets.service";
import { Asset } from "types/Asset";

interface FuelCard {
  label: string;
  value: string;
}

interface FuelCardSelectProps {
  value: string[];
  onChange: (selected: string[]) => void;
  isRequired?: boolean;
  label?: string;
  fetchAll?: boolean;
}

const FuelCardSelect: React.FC<FuelCardSelectProps> = ({
  value,
  onChange,
  isRequired,
  label,
  fetchAll = false,
}) => {
  const { t } = useTranslation();
  const [collection, setCollection] = useState<FuelCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { companyId } = useParams<{ companyId: string }>();
  const [error, setError] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>(value || []);
  const [suggestion, setSuggestion] = useState<string>("");

  useEffect(() => {
    // Update selected tags when value prop changes
    if (Array.isArray(value)) {
      setSelectedTags(value);
    }
  }, [value]);

  const handleMultiSelectChange = useCallback(
    (selected: string | undefined) => {
      if (selected) {
        const updatedSelection = selectedTags.includes(selected)
          ? selectedTags.filter((tag) => tag !== selected)
          : [...selectedTags, selected];
        setSelectedTags(updatedSelection);
        setSuggestion("");
        onChange(updatedSelection);
      }
    },
    [selectedTags, onChange]
  );

  const handleRemoveTag = useCallback(
    (tag: string) => () => {
      const updatedSelection = selectedTags.filter((item) => item !== tag);
      setSelectedTags(updatedSelection);
      onChange(updatedSelection);
    },
    [selectedTags, onChange]
  );

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        // Use fetchAll to get all company assets, or fetchUserAssets for user-specific
        const assets = companyId
          ? fetchAll
            ? await fetchAllAssets(companyId, {})
            : await fetchUserAssets(companyId)
          : [];
        const dataCollection = assets.map((row: Asset) => ({
          label: `${row.name}`,
          value: row.id,
        }));

        dataCollection.unshift({ label: " - ", value: "" });
        setCollection(dataCollection);
      } catch (e) {
        setError(t("fetch_error") || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId, fetchAll]);

  const optionsMarkup = collection.map((option, index) => {
    const isSelected = selectedTags.includes(option.value);
    return (
      <Listbox.Option
        key={index}
        value={option.value}
        selected={isSelected}
        accessibilityLabel={option.label}
      >
        <Listbox.TextOption selected={isSelected}>
          {option.label}
        </Listbox.TextOption>
      </Listbox.Option>
    );
  });

  return (
    <div>
      <Combobox
        allowMultiple
        activator={
          <Combobox.TextField
            label={label || t("fuel_card_select")}
            suggestion={suggestion}
            error={
              error ||
              (isRequired && selectedTags.length === 0
                ? t("fuel_card_is_required")
                : "")
            }
            autoComplete="off"
            verticalContent={
              <LegacyStack spacing="extraTight">
                {selectedTags.map((tag, index) => (
                  <Tag key={index} onRemove={handleRemoveTag(tag)}>
                    {collection.find((item) => item.value === tag)?.label}
                  </Tag>
                ))}
              </LegacyStack>
            }
          />
        }
      >
        <Listbox onSelect={handleMultiSelectChange}>{optionsMarkup}</Listbox>
      </Combobox>
    </div>
  );
};

export default FuelCardSelect;
